"use client";

import { useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useGetSubscriptionLobbyContentQuery } from "@/app/redux/features/api/common";
import { usePassWinners, formatMonthYear } from "@/app/hooks/usePassWinners";
import { WinnerTestimonial } from "../types";
import WinnerCard from "./WinnerCard";

export default function WinnersSection() {
  const { data: lobbyContent } = useGetSubscriptionLobbyContentQuery();
  const winnersCount = lobbyContent?.totalWinnerCount || "";
  const { winners: rawWinners } = usePassWinners();
  const swiperRef = useRef<any>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const winners: WinnerTestimonial[] = useMemo(
    () =>
      rawWinners.map((w, i) => ({
        id: String(i),
        bannerImage: w.Photo,
        prizeImage: w.PrizeImages?.[0] ?? "",
        prize: w.Prize,
        name: w.Name,
        memberType: "BOTB Pass Member",
        date: formatMonthYear(w.Date.MY),
        quote: w.StoryPreview,
      })),
    [rawWinners]
  );

  function handlePrev() {
    swiperRef.current?.slidePrev();
  }

  function handleNext() {
    swiperRef.current?.slideNext();
  }

  return (
    <section className="bg-white w-full py-6 lg:py-10">
      <div className="max-w-[1356px] mx-auto flex flex-col gap-2 lg:gap-6 items-center">
        <div className="w-full px-[18px] lg:px-2">
          <div className="w-full flex flex-col gap-2 lg:gap-4 items-center text-center">
            <h2 className="!m-0 p-0 normal-case max-w-[264px] lg:max-w-none text-2xl lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10 text-dark-1">
              BOTB Pass members{" "}
              <span className="text-primary">
                <br className="block lg:hidden" />
                who won big
              </span>
            </h2>
            <p className="m-0 p-0 text-base lg:text-xl font-normal font-jost leading-5.5 lg:leading-6.5 text-secondary">
              {winnersCount} winners and counting. Your name could be next.
            </p>
          </div>
        </div>

        <div className="hidden lg:flex gap-2 items-center self-end lg:px-2">
          <button
            onClick={handlePrev}
            disabled={isBeginning}
            aria-label="Previous winners"
            className={`size-6 flex items-center justify-center rounded-[4px] bg-white${isBeginning ? " opacity-50 cursor-default" : " cursor-pointer"}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="rotate-180" aria-hidden="true">
              <path
                d="M6 3L11 8L6 13"
                stroke="#28282B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            onClick={handleNext}
            disabled={isEnd}
            aria-label="Next winners"
            className={`size-6 flex items-center justify-center rounded-[4px] bg-white${isEnd ? " opacity-50 cursor-default" : " cursor-pointer"}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M6 3L11 8L6 13"
                stroke="#28282B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="w-full overflow-hidden lg:-mx-2 lg:px-2">
          <Swiper
            modules={[Pagination]}
            slidesPerView={1.16}
            spaceBetween={16}
            slidesOffsetBefore={18}
            slidesOffsetAfter={18}
            pagination={{
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 5,
            }}
            breakpoints={{ 1024: { slidesPerView: 3, spaceBetween: 16, slidesOffsetBefore: 0, slidesOffsetAfter: 0 } }}
            onSwiper={swiper => {
              swiperRef.current = swiper;
            }}
            onSlideChange={swiper => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onUpdate={swiper => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            style={
              {
                "--swiper-pagination-bottom": "0px",
                "--swiper-pagination-bullet-horizontal-gap": "3px",
              } as React.CSSProperties
            }
            className="w-full !overflow-visible !pb-6 lg:!pb-8 [--swiper-pagination-bullet-size:8px] lg:[--swiper-pagination-bullet-size:10px]">
            {winners.map(winner => (
              <SwiperSlide key={winner.id}>
                <WinnerCard {...winner} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
